// How did one manual "Run AI" request turn out, and what do we tell the user?
//
// Extracted rather than inlined for the same reason as archiveOutcome: this is the
// part that is easy to get wrong, and it is the part worth testing on its own —
// UploadedRecords itself cannot be imported under the bun harness today.
//
// `crud` resolves `{ ...axiosError, error: true }` on a non-2xx
// (actions/helpers/crud.js), so the per-document response is at
// `result.response`.
//
// The subtle case is the 409. POST /run_ai returns it for BOTH "a run is already
// under way" and "AI has already finished on this document", which are opposite
// things to tell a user — reporting a finished run as "already started" invites
// them to sit and wait for a run that will never report back. The controller
// disambiguates with an additive `error_code`
// (Api::V1::RecordsController::MANUAL_AI_ALREADY_COMPLETE_CODE); a 409 without one
// is treated as in-progress, which is the older of the two behaviors.
export const AI_RUN_ALREADY_COMPLETE_CODE = 'ai_run_already_complete';

export const MANUAL_AI_OUTCOMES = {
  started: 'started',
  alreadyRunning: 'alreadyRunning',
  alreadyComplete: 'alreadyComplete',
  failed: 'failed',
};

export const classifyManualAiResult = (result) => {
  if (!result?.error) return MANUAL_AI_OUTCOMES.started;

  const data = result?.response?.data;
  if (data?.error_code === AI_RUN_ALREADY_COMPLETE_CODE) return MANUAL_AI_OUTCOMES.alreadyComplete;
  if (result?.response?.status === 409) return MANUAL_AI_OUTCOMES.alreadyRunning;
  return MANUAL_AI_OUTCOMES.failed;
};

// Only `error` — the text json_error renders, which the endpoint writes
// deliberately ("AI can only be run on uploaded documents.", "This document has no
// pages to analyze."). `message` is deliberately ignored: on a 500 it is the
// generic "Internal server error", which tells the user nothing they can act on.
export const manualAiFailureReason = (result) => result?.response?.data?.error;

// A reason is only worth showing when it speaks for every failure in the batch.
// With mixed reasons, one document's message would misdescribe the others, so the
// summary falls back to the bare count.
export const sharedFailureReason = (reasons, failedCount) => {
  const present = reasons.filter(Boolean);
  if (present.length !== failedCount || present.length === 0) return null;

  const unique = new Set(present);
  return unique.size === 1 ? [...unique][0] : null;
};

export const manualAiRunSummary = ({
  started = 0, alreadyRunning = 0, alreadyComplete = 0, failed = 0, reason = null,
}) => {
  const details = [];
  if (alreadyRunning) details.push(`${alreadyRunning} already running`);
  if (alreadyComplete) details.push(`${alreadyComplete} already complete`);
  if (failed) details.push(reason ? `${failed} failed: ${reason}` : `${failed} failed`);
  const summary = `AI started on ${started} document${started === 1 ? '' : 's'}`;
  return details.length ? `${summary} (${details.join(', ')})` : summary;
};

export default manualAiRunSummary;
